/**
 * Thin browser Web SDK tracker (demo L2 write-key). Sends BEHAVIORAL web-only
 * events the server doesn't already emit — `web_page_view`, `scroll_depth`,
 * `banner_view`, `product_impression`, `time_on_page` — straight to the CDP engine
 * by public write-key (`POST /v1/events/batch`, header `X-CDP-Write-Key`), same-
 * origin via the storefront proxy. It NEVER sends value/commerce events
 * (order_placed etc.) — those stay server-side (a public write-key is refused for
 * them anyway), so there's no double-counting with the BFF's viewed/cart/checkout.
 *
 * The visitor is identified by a stable `cookie_id` handed out by the BFF
 * (`/api/v1/cdp/web-context`) so these events land on the SAME CDP profile as the
 * server events and the addressed experiences.
 */

export interface WebEventInput {
  event_type: string;
  payload?: Record<string, unknown>;
  /** ISO 8601; defaults to now at build time. */
  occurred_at?: string;
}

export interface CdpEventBody {
  event_type: string;
  source: string;
  occurred_at: string;
  identifiers: Array<{ type: string; value: string }>;
  payload?: Record<string, unknown>;
}

/** Pure builder (unit-tested): a Web-SDK event → the `/v1/events` body shape. */
export function buildEvent(input: WebEventInput, cookieId: string, nowIso: string, source = "web_js"): CdpEventBody {
  return {
    event_type: input.event_type,
    source,
    occurred_at: input.occurred_at ?? nowIso,
    identifiers: [{ type: "cookie_id", value: cookieId }],
    ...(input.payload ? { payload: input.payload } : {}),
  };
}

export interface TrackerOptions {
  /** Base URL for `/v1/events*`. Default "" (same-origin proxy). */
  base?: string;
  /** Public write-key (`cdp_wk_…`, scope `ingest`). */
  writeKey: string;
  /** Stable visitor cookie_id (from the BFF web-context). */
  cookieId: string;
  /** Debounce before an auto-flush (ms). Default 1500. */
  flushMs?: number;
  /** Max events per batch (engine caps at 500). Default 20. */
  maxBatch?: number;
  fetchImpl?: typeof fetch;
}

export class WebSdkTracker {
  private base: string;
  private writeKey: string;
  private cookieId: string;
  private flushMs: number;
  private maxBatch: number;
  private fetchImpl: typeof fetch;
  private queue: CdpEventBody[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: TrackerOptions) {
    this.base = (opts.base ?? "").replace(/\/$/, "");
    this.writeKey = opts.writeKey;
    this.cookieId = opts.cookieId;
    this.flushMs = opts.flushMs ?? 1500;
    this.maxBatch = opts.maxBatch ?? 20;
    this.fetchImpl =
      opts.fetchImpl ??
      (globalThis.fetch ? globalThis.fetch.bind(globalThis) : (undefined as unknown as typeof fetch));
  }

  /** Queue a behavioral event; auto-flushes after a short debounce or when the batch fills. */
  track(event_type: string, payload?: Record<string, unknown>): void {
    this.queue.push(buildEvent({ event_type, payload }, this.cookieId, new Date().toISOString()));
    if (this.queue.length >= this.maxBatch) {
      void this.flush();
      return;
    }
    if (this.timer == null) {
      this.timer = setTimeout(() => void this.flush(), this.flushMs);
    }
  }

  /** Flush the queue to `POST /v1/events/batch` (keepalive so an in-flight unload survives). */
  async flush(): Promise<void> {
    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, this.maxBatch);
    try {
      await this.fetchImpl(`${this.base}/v1/events/batch`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CDP-Write-Key": this.writeKey,
        },
        body: JSON.stringify({ events }),
      });
    } catch {
      /* fire-and-forget: dropping demo telemetry must never break the page */
    }
    // If more piled up past the batch cap, keep draining.
    if (this.queue.length > 0) this.timer = setTimeout(() => void this.flush(), this.flushMs);
  }
}
